import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "../../utils/api/axiosInstance";
import PostCard from "../profile/PostCard";
import CreatePostModal from "../profile/CreatePostModal";
import { FaImage, FaPlus, FaPen } from "react-icons/fa";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { usePublicPages } from "../../contexts/PublicPagesContext";
import styles from "../../styles/components/product/ProductPostsSection.module.css";

const ProductPostsSection = ({
  productId,
  product,
  className,
  gridClassName,
  postCardClassName,
  PostCardComponent = PostCard,
  useDefaultStyles = true,
}) => {
  const { user: currentUser } = useUserAuth();
  const { isPageEnabled } = usePublicPages();
  const feedEnabled = isPageEnabled("feed");
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!feedEnabled) return;
    if (productId) {
      fetchPosts();
    }
  }, [productId, feedEnabled]);

  const fetchPosts = async (pageNum = 1) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const res = await api.get(`/posts/product/${productId}`, {
        params: { page: pageNum, limit: 3 },
      });

      if (pageNum === 1) {
        setPosts(res.data.posts || []);
      } else {
        setPosts((prev) => [...prev, ...(res.data.posts || [])]);
      }

      setHasMore(res.data.pagination.page < res.data.pagination.pages);
      setPage(pageNum);
    } catch (err) {
      console.error("Error fetching product posts:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchPosts(page + 1);
    }
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
  };

  const handleLikeUpdate = (postId, isLiked, likeCount) => {
    setPosts((prev) =>
      prev.map((p) =>
        p._id === postId
          ? {
              ...p,
              isLiked,
              likeCount,
              likes: isLiked
                ? [...(p.likes || []), currentUser?.id]
                : (p.likes || []).filter((id) => id !== currentUser?.id),
            }
          : p
      )
    );
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts((prev) =>
      prev.map((p) => (p._id === updatedPost._id ? updatedPost : p))
    );
  };

  const handleCommentNavigation = (postId) => {
    const fromPath = `${location.pathname}${location.search}`;
    navigate(`/post/${postId}`, {
      state: {
        from: fromPath,
        scrollToComments: true
      }
    });
  };

  const sectionClasses = [
    useDefaultStyles && styles.productPostsSection,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const gridClasses = [useDefaultStyles && styles.postsGrid, gridClassName]
    .filter(Boolean)
    .join(" ");

  const handlePostCreated = (newPost) => {
    setShowCreateModal(false);
    if (newPost) setPosts((prev) => [newPost, ...prev]);
  };

  const postAboutBtn = currentUser ? (
    <button
      type="button"
      className={styles.postAboutBtn}
      onClick={() => setShowCreateModal(true)}
    >
      <FaPen size={11} /> Post about this
    </button>
  ) : null;

  if (!feedEnabled) {
    return null;
  }

  if (loading) {
    return (
      <div className={sectionClasses}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeaderTop}>
            <h3>See How Others Use This Product</h3>
            {postAboutBtn}
          </div>
          <p className={styles.sectionSubtitle}>
            Real posts from users showcasing this product
          </p>
        </div>
        <div className={styles.loading}>Loading posts...</div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className={sectionClasses}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeaderTop}>
            <h3>See How Others Use This Product</h3>
            {postAboutBtn}
          </div>
          <p className={styles.sectionSubtitle}>
            Be the first to share how you use this product!
          </p>
        </div>
        <div className={styles.emptyState}>
          <FaImage className={styles.emptyIcon} />
          <p>No posts yet for this product</p>
          {currentUser && (
            <button
              type="button"
              className={styles.createPostButton}
              onClick={() => setShowCreateModal(true)}
            >
              <FaPlus /> Create a Post
            </button>
          )}
        </div>
        {showCreateModal && (
          <CreatePostModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onPostCreated={handlePostCreated}
            initialProducts={product ? [product] : []}
          />
        )}
      </div>
    );
  }

  return (
    <div className={sectionClasses}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionHeaderTop}>
          <h3>See How Others Use This Product</h3>
          {postAboutBtn}
        </div>
        <p className={styles.sectionSubtitle}>
          {posts.length} {posts.length === 1 ? "post" : "posts"} from users
          showcasing this product
        </p>
      </div>
      <div className={gridClasses}>
        {posts.map((post) => (
          <PostCardComponent
            key={post._id}
            post={post}
            onDelete={handlePostDeleted}
            onLikeUpdate={handleLikeUpdate}
            onPostUpdated={handlePostUpdated}
            className={postCardClassName}
            commentAction="navigate"
            onCommentNavigate={() => handleCommentNavigation(post._id)}
          />
        ))}
      </div>
      {hasMore && (
        <div className={styles.loadMoreContainer}>
          <button
            className={styles.loadMoreButton}
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading..." : <>Show more posts<span className={styles.expandIcon}>▼</span></>}
          </button>
        </div>
      )}

      {showCreateModal && (
        <CreatePostModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onPostCreated={handlePostCreated}
          initialProducts={product ? [product] : []}
        />
      )}
    </div>
  );
};

export default ProductPostsSection;
